import Checkbox from "@material-ui/core/Checkbox";
import type { CSSProperties, ReactNode } from "react";

import CustomLoader from "../../CustomLoader";
import type { TableColumn } from "./types";

interface TableBodyProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string | number;

  selection: boolean;
  selectedKeySet: Set<string | number>;
  onToggleRow: (row: T) => void;
  isRowSelectable: (row: T) => boolean;

  isLoading: boolean;
  emptyMessage: string;

  rowStyle?: CSSProperties | ((row: T) => CSSProperties);
}

const getCellValue = <T,>(row: T, field?: string): ReactNode => {
  if (!field) return null;

  const value = (row as Record<string, unknown>)[field];

  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "boolean") return value ? "Yes" : "No";

  return String(value);
};

const TableBody = <T,>({
  columns,
  data,
  rowKey,
  selection,
  selectedKeySet,
  onToggleRow,
  isRowSelectable,
  isLoading,
  emptyMessage,
  rowStyle,
}: TableBodyProps<T>) => {
  const colSpan = columns.length + (selection ? 1 : 0);
  const calculatedColumnWidth = `calc(100% / ${Math.max(columns.length, 1)})`;

  if (isLoading && data.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={colSpan}
            style={{
              padding: "6px 24px 6px 16px",
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              boxSizing: "border-box",
            }}
          >
            <CustomLoader />
          </td>
        </tr>
      </tbody>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={colSpan}
            style={{
              padding: "6px 24px 6px 16px",
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              boxSizing: "border-box",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: "14px" }}>{emptyMessage}</span>
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((row) => {
        const key = rowKey(row);
        const selectable = isRowSelectable(row);
        const checked = selectedKeySet.has(key);

        const customRowStyle =
          typeof rowStyle === "function" ? rowStyle(row) : rowStyle;

        return (
          <tr
            key={key}
            style={{
              transition: "all ease 300ms",
              backgroundColor: "#FFF",
              color: "#000000",
              fontWeight: "normal",
              fontSize: "13px",
              ...customRowStyle,
            }}
          >
            {selection && (
              <td
                style={{
                  width: 26,
                  padding: 0,
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                  verticalAlign: "inherit",
                  boxSizing: "border-box",
                  textAlign: "left",
                }}
              >
                <Checkbox
                  size="small"
                  color="primary"
                  checked={checked}
                  disabled={!selectable}
                  onChange={() => onToggleRow(row)}
                  style={{
                    padding: "4px",
                    marginLeft: 5,
                  }}
                  inputProps={{
                    "aria-label": `select row ${String(key)}`,
                  }}
                />
              </td>
            )}

            {columns.map((column, index) => {
              const isLastColumn = index === columns.length - 1;

              return (
                <td
                  key={`${String(column.field ?? "column")}-${index}`}
                  style={{
                    width: calculatedColumnWidth,
                    padding: isLastColumn
                      ? "6px 16px 6px 16px"
                      : "6px 24px 6px 16px",
                    borderBottom: "1px solid rgba(224, 224, 224, 1)",
                    verticalAlign: "inherit",
                    boxSizing: "border-box",
                    color: "inherit",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    fontWeight: "inherit",
                    lineHeight: 1.43,
                    letterSpacing: "0.01071em",
                    textAlign: column.align ?? "left",
                    ...column.cellStyle,
                  }}
                >
                  {column.render
                    ? column.render(row)
                    : getCellValue(row, column.field)}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
};

export default TableBody;
